// login.component.ts
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';
import { trigger, transition, style, animate } from '@angular/animations';

// Importa tu servicio de autenticación
import { AuthService } from '../../auth/services/auth.service';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  animations: [
    trigger('fadeOverlay', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class LoginComponent implements OnInit {
  isLoading: boolean = false;
  loginForm!: FormGroup;
  registerForm!: FormGroup;

  loginErrorMsg = '';
  registerErrorMsg = '';

  constructor(
    private fb: FormBuilder,
    private title: Title,
    private meta: Meta,
    private authService: AuthService,
    private router: Router
  ) {
    // SEO (opcional)
    this.title.setTitle('Inicia sesión o regístrate | Tiger Zen Do');
    this.meta.addTags([
      {
        name: 'description',
        content: 'Accede a tu cuenta o regístrate para disfrutar de nuestras clases y servicios exclusivos.'
      },
      {
        name: 'keywords',
        content: 'gimnasio, registro, clases, artes marciales, fitness'
      }
    ]);
  }

  ngOnInit(): void {
    // Formulario LOGIN
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });

    // Rellenar email si “Recuérdame” ya estaba guardado
    const savedEmail = localStorage.getItem('savedEmail');
    if (savedEmail) {
      this.loginForm.patchValue({ email: savedEmail, rememberMe: true });
    }

    // Formulario REGISTER
    this.registerForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, {
      // Validador de grupo para revisar que password y confirmPassword coincidan
      validators: [this.matchPasswords]
    });
  }

  // --------------------------------------------
  // LOGIN
  // --------------------------------------------
  onLoginSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    const { email, password, rememberMe } = this.loginForm.value;

    // Manejo de “Recuérdame”
    if (rememberMe) {
      localStorage.setItem('savedEmail', email);
    } else {
      localStorage.removeItem('savedEmail');
    }

    // Llamar servicio de autenticación para login
    this.authService.login({ email, password }).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res && res.token) {
          this.authService.setToken(res.token);
          this.router.navigate(['/store/products']);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error en login:', err);
        this.loginErrorMsg = 'Credenciales inválidas. Intenta de nuevo.';
      }
    });
  }

  // Getters para mostrar errores en Login
  get emailFieldLogin() { return this.loginForm.get('email'); }
  get passwordFieldLogin() { return this.loginForm.get('password'); }


  // --------------------------------------------
  // REGISTER
  // --------------------------------------------
  onRegisterSubmit(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }
    this.isLoading = true;

    const { email, password } = this.registerForm.value;
    // Llamar servicio de autenticación para registro
    this.authService.register({ email, password }).subscribe({
      next: (res) => {
        this.isLoading = false;
        // Por ejemplo, si el backend retorna { token: '...', user: ... }
        if (res && res.token) {
          // Guardamos token, redirigimos, etc.
          this.authService.setToken(res.token);
          this.router.navigate(['/store/products']);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error en registro:', err);
        this.registerErrorMsg = 'Error al registrarse. Revisa tus datos.';
      }
    });
  }

  // Validador de grupo para confirmar password
  matchPasswords(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirm = control.get('confirmPassword')?.value;

    if (password && confirm && password !== confirm) {
      // Si son diferentes, retornamos un objeto con el error “mismatch”
      return { mismatch: true };
    }
    return null; // No hay error
  }

  // Getters para mostrar errores en Register
  get emailFieldRegister() { return this.registerForm.get('email'); }
  get passwordFieldRegister() { return this.registerForm.get('password'); }
  get confirmPasswordFieldRegister() { return this.registerForm.get('confirmPassword'); }
}